import React, { useCallback, useEffect, useState } from "react";
import { emitToast } from "./Toast";
import { refreshBadgeCounts } from "./notifications";
import "./homeSuggestions.css";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// ホームの「承認待ち」セクション（会話から拾った予定/TODO候補の振り分け）。
// カレンダーとTODOの間に置く。社員/経営者 共通（ホーム自体が共通のため出し分け不要）。
// 方針＝最小・手数優先（案B）:
//  - 枠(カード)なしのフラットなリスト＋区切り線。
//  - 件名・日時は“最初から編集できる薄いテキスト”。編集モードの開閉なし＝触る→ボタンだけ。
//  - 予定/TODO/却下はテキストのアクション。却下は確認ダイアログなしで即実行（DBには残る）。
//  - 予定は「開始＋終日」のみ扱う（終了時刻は triage では扱わない）。
//    終日の初期値は抽出結果 allDay（時刻あり→開始時刻/時刻なし→終日）。トグルで上書き可。
//  - 0件のときはセクションごと非表示＝ホームを汚さない。

const API_URL = process.env.REACT_APP_API_URL || "";

const toLocalInput = (iso) => (iso ? String(iso).slice(0, 16) : ""); // datetime-local 用 "YYYY-MM-DDTHH:mm"
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : ""); // date 用 "YYYY-MM-DD"

// 却下＝ゴミ箱アイコン（TodoCard と同じ流儀の SVG。mp-btn が 16px/currentColor で描画）
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// 1候補の行。件名・日時・終日を行内で保持し、3アクションで確定する。
function SuggestionRow({ item, onResolve }) {
  const [title, setTitle] = useState(item.title);
  const [allDay, setAllDay] = useState(!!item.allDay);
  const [dt, setDt] = useState(toLocalInput(item.start)); // 時刻あり用
  const [d, setD] = useState(toDateInput(item.start)); // 終日用（日付のみ）
  const [busy, setBusy] = useState(false);

  const toggleAllDay = () => {
    if (allDay) {
      // 終日→時刻あり：時刻が無ければ日付＋09:00で補う（既存の時刻は保持）
      if (!dt && d) setDt(`${d}T09:00`);
    } else {
      // 時刻あり→終日：日付が無ければ時刻の日付部分から補う
      if (!d && dt) setD(dt.slice(0, 10));
    }
    setAllDay((v) => !v);
  };

  const resolve = async (action) => {
    if (busy) return;
    const payload = { id: item.id, action, title: (title || "").trim() || item.title };
    if (action === "event") {
      const start = allDay ? d : dt;
      if (!start) {
        emitToast("予定にするには日時を入れてください");
        return;
      }
      payload.start = start;
      payload.allday = allDay;
    } else if (action === "todo") {
      // TODOの締切は日付部分（終日なら日付、時刻ありなら日付部分）。無ければ空。
      payload.due = allDay ? d : dt ? dt.slice(0, 10) : "";
    }
    setBusy(true);
    try {
      await onResolve(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sg-row">
      {/* 1行目：件名（左）＋却下＝ゴミ箱（右端） */}
      <div className="sg-top">
        <input
          className="sg-title"
          value={title}
          placeholder="件名"
          aria-label="件名"
          onChange={(e) => setTitle(e.target.value)}
        />
        <button
          type="button"
          className="mp-todo-ic mp-todo-ic-danger"
          aria-label="却下（削除）"
          disabled={busy}
          onClick={() => resolve("reject")}
        >
          <TrashIcon />
        </button>
      </div>
      {/* 2行目：日付＋終日（カレンダーの横）… 予定 / TODO（右） */}
      <div className="sg-line">
        {allDay ? (
          <input
            className="sg-dt"
            type="date"
            value={d}
            aria-label="日付"
            onChange={(e) => setD(e.target.value)}
          />
        ) : (
          <input
            className="sg-dt"
            type="datetime-local"
            value={dt}
            aria-label="開始日時"
            onChange={(e) => setDt(e.target.value)}
          />
        )}
        <button
          type="button"
          className={`sg-toggle${allDay ? " on" : ""}`}
          aria-pressed={allDay}
          onClick={toggleAllDay}
        >
          終日
        </button>
        <span className="sg-acts">
          <button className="mp-btn mp-ghost sg-btn" disabled={busy} onClick={() => resolve("event")}>予定</button>
          <button className="mp-btn mp-ghost sg-btn" disabled={busy} onClick={() => resolve("todo")}>TODO</button>
        </span>
      </div>
    </div>
  );
}

function HomeSuggestions({ onResolved }) {
  const [items, setItems] = useState(null); // null=未取得

  const refresh = useCallback(() => {
    fetch(`${API_URL}/api/employee/suggestions/`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setItems(data.results || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const doResolve = async (payload) => {
    const res = await fetch(`${API_URL}/api/employee/suggestions/resolve/`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // BEの理由をそのまま出す。Inboxは全社共有なので「この候補は既に振り分け済みです」(409)
      // ＝他の人が先に処理した、が実際に起きる。固定文言だと理由が分からない。
      emitToast(await apiErrorMessage(res, "振り分けに失敗しました"), "error");
      return;
    }
    emitToast(
      payload.action === "todo"
        ? "TODOに追加しました"
        : payload.action === "event"
        ? "予定に追加しました"
        : "却下しました"
    );
    setItems((prev) => (prev || []).filter((x) => x.id !== payload.id));
    // 振り分け＝Inboxが1件減った。ホームのバッジ（状態由来）も合わせて減らす。
    // TODOへ振り分けた場合は未対応TODOが増えるため、TODOバッジも同時に更新される。
    refreshBadgeCounts();
    if (onResolved) onResolved(payload.action);
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="sg-sec">
      <div className="mp-rhead">
        <h2>承認待ち <span className="sg-count">{items.length}</span></h2>
      </div>
      <div className="sg-list">
        {items.map((it) => (
          <SuggestionRow key={it.id} item={it} onResolve={doResolve} />
        ))}
      </div>
    </div>
  );
}

export default HomeSuggestions;
